import { createFileRoute, Outlet } from '@tanstack/react-router';

import {
  ensureAuthMe,
  useIdentityChangeSync,
  useSyncAuthMeToGTM,
  useSyncAuthMeToPostHog,
  useSyncAuthMeToSentry,
  useTabFocusAuth
} from 'shared/hooks';
import { ensureCustomerCapabilities, useCustomerCapabilities } from 'shared/modules/capabilities';
import { ImpersonationBanner } from 'shared/modules/impersonation';
import { OldPlatformRedirectModal } from 'shared/modules/old-platform-redirect';
import { useResumeRMReportSetups } from 'shared/modules/run-reports';
import { identifyPostHogUser, redirectIfSessionExpired } from 'shared/utils';

export const Route = createFileRoute('/_authenticated')({
  // The in-memory access token is always empty after a page reload, while the httpOnly cookie may still be valid.
  // `ensureAuthMe` triggers the cookie-based refresh via the auth middleware; a
  // 401 fires `onSessionExpired` and is also
  // converted here into a router `redirect` to the same session-expired login
  // URL, so the router navigates instead of painting the error screen.
  beforeLoad: async ({ context, location }) => {
    // Customer capabilities load alongside auth/me so the two requests don't queue; a failed read
    // resolves to `null` and never blocks login.
    const customerCapabilitiesPromise = ensureCustomerCapabilities(context.queryClient);
    const authUser = await ensureAuthMe(context.queryClient).catch((error: unknown) =>
      redirectIfSessionExpired(error, location.href)
    );

    await identifyPostHogUser(authUser);

    const customerCapabilities = await customerCapabilitiesPromise;

    return { authUser, customerCapabilities };
  },
  component: AuthenticatedLayout
});

function AuthenticatedLayout() {
  useSyncAuthMeToGTM();
  useSyncAuthMeToSentry();
  useSyncAuthMeToPostHog();
  useTabFocusAuth();
  useIdentityChangeSync();
  // App-wide observer: keeps the customer capabilities refetching on tab refocus once stale.
  useCustomerCapabilities();
  useResumeRMReportSetups();

  return (
    <>
      <ImpersonationBanner />
      <Outlet />
      {/* This is temporary component used before we migrate all tools to the new platform */}
      <OldPlatformRedirectModal />
    </>
  );
}
